import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import FrameColor from "./components/FrameColor";
import SiteName from "./components/SiteName";
import Logo from "./components/Logo";
import UseAtLogin from "./components/UseAtLogin";
import { DEFAULT_SITE_NAME, DEFAULT_FRAME_COLOR, isValidFrameColorFormat, CLIENT_CODE_STORAGE_KEY } from "./utils";
import {
  useUploadGroupStyleLogoMutation,
  useRemoveGroupStyleLogoMutation,
  useUpdateGroupStyleMutation,
} from "../../../services/groupAPI";
import { toast } from "react-toastify";
import { useAppSelector } from "../../../app/hooks";
import { selectGroupById } from "../../../features/groups/groupSlice";
import { LoadingSpinner } from "../../../utils/StateDisplay";
import useGetLanguage from "../../../utils/useGetLanguage";
import { LanguageText } from "../../../dictionary/GroupsText";

export const GroupStyle: React.FC = () => {
  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });
  const { groupid } = useParams<{ groupid: string }>();
  const group = useAppSelector((state) => selectGroupById(state, groupid ?? ""));
  const [uploadLogo, { isLoading: isUploadLogoLoading, error: uploadLogoError }] =
    useUploadGroupStyleLogoMutation();
  const [removeLogo, { isLoading: isRemoveLogoLoading, error: removeLogoError }] =
    useRemoveGroupStyleLogoMutation();
  const [updateStyle, { isLoading: isUpdateStyleLoading, error: isUpdateStyleError }] =
    useUpdateGroupStyleMutation();

  const [frameColor, setColor] = useState<string>(
    isValidFrameColorFormat(group?.style?.frame_color)
      ? group?.style?.frame_color!
      : DEFAULT_FRAME_COLOR
  );

  const [siteName, setSiteName] = useState<string>(
    group?.style?.site_name ?? DEFAULT_SITE_NAME
  );
  const [logo, setLogo] = useState<File | string | null>(group?.style?.logo?.url || null);
  const [useAtLogin, setUseAtLogin] = useState<boolean>(group?.style?.default ?? false);

  const handleStyleChange = async () => {
    if (groupid) {
      try {
        await updateStyle({
          groupid,
          style: {
            frame_color: frameColor,
            site_name: siteName,
            default: useAtLogin,
          },
        }).unwrap();
        
        // Update localStorage based on useAtLogin value
        if (useAtLogin && group?.client_code) {
          localStorage.setItem(CLIENT_CODE_STORAGE_KEY, group.client_code);
        } else {
          localStorage.removeItem(CLIENT_CODE_STORAGE_KEY);
        }
        
        toast.success(
          language?.groupStyle?.general?.messages?.successfulStyleUpdate ||
            "Style updated successfully"
        );
      } catch (error) {
        toast.error(
          language?.groupStyle?.general?.messages?.unsuccessfulStyleUpdate ||
            "Could not update style. Please try agagin later"
        );
      }
    }
  };

  const handleLogoUpload = async (file: File) => {
    setLogo(file);
    if (groupid && file && file instanceof File) {
      try {
        await uploadLogo({ groupid, logo: file }).unwrap();
        toast.success(
          language?.groupStyle?.general?.messages?.successfulLogoUpload ||
            "Logo updated successfully"
        );
      } catch (error) {
        toast.error(
          language?.groupStyle?.general?.messages?.unsuccessfulLogoUpload ||
            "Could not upload Logo. Please try again later"
        );
      }
    }
  };

  const handleRemoveLogo = async () => {
    setLogo(null);
    const logoID = group?.style?.logo?.logo_id;
    if (groupid && logoID) {
      try {
        await removeLogo({ groupid, logoID }).unwrap();
        toast.success(
          language?.groupStyle?.general?.messages?.successfulLogoRemoval ||
            "Logo removed successfully"
        );
      } catch (error) {
        toast.error(
          language?.groupStyle?.general?.messages?.unsuccessfulLogoRemoval ||
            "Could not remove Logo. Please try again later"
        );
      }
    }
  };

  useEffect(() => {
    if (group) {
      setLogo(group?.style?.logo?.url ?? null);
      setSiteName(group?.style?.site_name ?? DEFAULT_SITE_NAME);
      setColor(
        isValidFrameColorFormat(group?.style?.frame_color)
          ? group?.style?.frame_color!
          : DEFAULT_FRAME_COLOR
      );
      setUseAtLogin(group?.style?.default ?? false);
    }
  }, [group]);

  if (!group) return <LoadingSpinner />;
  return (
    <div className="w-full max-w-full min-w-0 px-1 sm:px-2 md:px-3">
      <div className="flex flex-col items-center gap-y-2 sm:gap-y-3 md:gap-y-4 max-w-[280px] sm:max-w-[320px] md:max-w-[360px] w-full min-w-0 mx-auto bg-gray-100 rounded-md sm:rounded-lg shadow p-2 sm:p-3 md:p-4">
        <Logo src={logo} onChange={handleLogoUpload} onDelete={handleRemoveLogo} />
        <FrameColor
          color={frameColor}
          onChange={setColor}
          onReset={() => setColor(DEFAULT_FRAME_COLOR)}
        />
        <SiteName name={siteName} onChange={setSiteName} onClear={() => setSiteName("")} />
        <UseAtLogin
          checked={useAtLogin}
          onChange={setUseAtLogin}
          clientCode={group?.client_code}
          groupId={groupid}
        />
        <button
          type="button"
          onClick={handleStyleChange}
          className="px-4 sm:px-6 md:px-8 py-1 sm:py-1.5 rounded-md bg-green-600 text-white font-medium hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 transition disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm md:text-base mx-auto"
          disabled={isUpdateStyleLoading}
        >
          {isUpdateStyleLoading
            ? `${language?.groupStyle?.general?.saving}...` || "Saving..."
            : language?.groupStyle?.general?.save || "Save"}
        </button>
      </div>
    </div>
  );
};
